import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { capitalizeFirstLetter } from 'utils/capitalize-first-letter';
import { formatDate } from 'utils/dayjs';

import {
  type GetDisbursementStatsPayoutRequestsReasonCodesParams,
  type PayoutReasonCodeStats,
  useGetDisbursementStatsPayoutRequestsReasonCodes
} from 'api';

import { useOrganisationId } from 'hooks/context';
import { useGlobalFilters } from 'hooks/useGlobalFilters';

import { DrilldownTable } from 'components/ui/organisms/DrilldownTable/DrilldownTable';

type CategoryKeyFromApi = keyof PayoutReasonCodeStats;

export const STATE_CATEGORIES_TO_SHOW = [
  'returned',
  'rejected',
  'action_required',
  'received',
  'processing',
  'pending',
  'completed'
] as const satisfies ReadonlyArray<CategoryKeyFromApi>;

export const getCategoryLabel = (code: CategoryKeyFromApi) => {
  return capitalizeFirstLetter(code.replace('_', ' '));
};

export const DrilldownTables = () => {
  const { filters } = useGlobalFilters();
  const organisationId = useOrganisationId<number>();
  const navigate = useNavigate();

  const queryParams: GetDisbursementStatsPayoutRequestsReasonCodesParams =
    useMemo(
      () => ({
        created_after: filters.dateRange.from
          ? formatDate(filters.dateRange.from, 'utc', 'iso')
          : undefined,
        created_before: filters.dateRange.to
          ? formatDate(filters.dateRange.to, 'utc', 'iso')
          : undefined
      }),
      [filters.dateRange]
    );

  const { data, isSuccess } = useGetDisbursementStatsPayoutRequestsReasonCodes(
    organisationId,
    queryParams
  );
  const responseData = data?.data;
  if (!isSuccess || !responseData) return null;

  const visibleCategories = STATE_CATEGORIES_TO_SHOW.filter(
    (category) => (responseData[category]?.meta?.count ?? 0) > 0
  );

  if (visibleCategories.length === 0) return null;

  return (
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
      {visibleCategories.map((category) => (
        <DrilldownTable
          key={category}
          stateId={category}
          title={getCategoryLabel(category)}
          data={responseData[category]}
          className="w-full"
          onHeaderClick={() =>
            navigate(`/org/${organisationId}/payouts?state=${category}`)
          }
        />
      ))}
    </div>
  );
};
